import type {
	CACheckboxRadioOption,
	CACheckboxType,
	CAInputDivider,
} from '@ds/interfaces'
import { preventAndStop } from '@vuejs/components/shared/utils'
import { defineComponent, type PropType } from 'vue'

import CABadge from '../CABadge'
import CADivider from '../CADivider'

/**
 * The `CACheckbox` component is used to display a checkbox with optional avatar, tags, and tooltip.
 * It supports various appearances such as bordered, centered-card, and checkbox-card.
 * @component CACheckbox
 *
 * @prop {string} [avatar=''] - The avatar to be displayed in the checkbox.
 * @prop {CACheckboxType} [checkboxAppearance='checkbox'] - The appearance style of the checkbox.
 * @prop {string} [dataTest=''] - The data attribute for testing.
 * @prop {string} [description=''] - The description text for the checkbox.
 * @prop {boolean} [disabled=false] - Whether the checkbox is disabled.
 * @prop {CAInputDivider | null} [divider=null] - The divider configuration for the checkbox.
 * @prop {CACheckboxRadioOption} [id] - The ID of the checkbox. This is required.
 * @prop {boolean} [indeterminate=false] - Whether the checkbox is in an indeterminate state.
 * @prop {string} [label=''] - The label for the checkbox.
 * @prop {boolean} [modelValue=false] - The selected state of the checkbox.
 * @prop {string} [name=''] - The name for the checkbox.
 * @prop {number} [tabindex=0] - The tabindex for the checkbox.
 * @prop {Array<string> | null} [tags=null] - The tags for the checkbox.
 * @prop {string} [tagsLabel=''] - The label for the tags.
 * @prop {string} [tooltip=''] - The tooltip text to show when hovering over the checkbox.
 * @prop {boolean} [truncateBadges=true] - Whether to truncate the badge text.
 * @prop {number | null} [truncatedLabelWidth=null] - the width for truncating the label text.
 * @prop {boolean} [useBorders=false] - Whether to show borders around the checkbox.
 *
 * @emit blur - Emits when the checkbox loses focus.
 * @emit focus - Emits when the checkbox gains focus.
 * @emit update:modelValue - Emits the selected state when the checkbox is clicked.
 *
 * @slot actions - Slot for additional actions to be displayed alongside the checkbox.
 * @slot label - Slot for custom label content.
 *
 */
export default defineComponent({
	name: 'CACheckbox',

	props: {
		/**
		 * Avatar to be displayed in the checkbox.
		 * @default ''
		 * @type {string}
		 */
		avatar: { default: '', type: String },
		/**
		 * The appearance style of the checkbox.
		 * @default 'checkbox'
		 * @type {CACheckboxType}
		 * @enum {('checkbox' | 'bordered' | 'centered-card' | 'checkbox-card')}
		 */
		checkboxAppearance: { default: 'checkbox', type: String as PropType<CACheckboxType> },
		/**
		 * Data attribute for testing.
		 * @default ''
		 * @type {string}
		 */
		dataTest: { default: '', type: String },
		/**
		 * Description text for the checkbox.
		 * @default ''
		 * @type {string}
		 */
		description: { default: '', type: String },
		/**
		 * Whether the checkbox is disabled.
		 * @default false
		 * @type {boolean}
		 */
		disabled: { default: false, type: Boolean },
		/**
		 * Divider configuration for the checkbox.
		 * @default null
		 * @type {CAInputDivider | null}
		 */
		divider: { optional: true, type: Object as PropType<CAInputDivider> },
		/**
		 * The ID of the checkbox.
		 * @required
		 * @type {CACheckboxRadioOption}
		 */
		id: {
			required: true,
			type: [
				Boolean,
				Number,
				String,
			] as PropType<CACheckboxRadioOption>,
		},
		/**
		 * Whether the checkbox is in an indeterminate state.
		 * @default false
		 * @type {boolean}
		 */
		indeterminate: { default: false, type: Boolean },
		/**
		 * The label for the checkbox.
		 * @default ''
		 * @type {string}
		 */
		label: { default: '', type: String },
		modelValue: { type: Boolean, default: false },
		/**
		 * The name for the checkbox.
		 * @default ''
		 * @type {string}
		 */
		name: { default: '', type: String },
		/**
		 * The tabindex for the checkbox.
		 * @default 0
		 * @type {number}
		 */
		tabindex: { default: 0, type: Number },
		/**
		 * Tags for the checkbox.
		 * @default null
		 * @type {Array<string> | null}
		 */
		tags: { default: null, type: Array as PropType<string[] | null> },
		/**
		 * The label for the tags.
		 * @default ''
		 * @type {string}
		 */
		tagsLabel: { default: '', type: String },
		/**
		 * Whether to show a tooltip on the checkbox.
		 * @default false
		 * @type {boolean}
		 */
		tooltip: { default: '', type: String },
		/**
		 * Whether to truncate the label text.
		 * @default false
		 * @type {boolean}
		 */
		truncateBadges: { default: true, type: Boolean },
		/**
		 * Whether to truncate the label text.
		 * @default false
		 * @type {boolean}
		 */
		truncatedLabelWidth: { default: null, type: Number as PropType<number | null> },
		/**
		 * Whether to show borders around the checkbox.
		 * @default false
		 * @type {boolean}
		 */
		useBorders: { default: false, type: Boolean },
	},

	emits: {
		blur: () => true,
		focus: () => true,
		'update:modelValue': (_value: boolean) => true,
	},

	data(): CAInputData {
		return {
			inputElement: null,
			isFocused: false,
		}
	},

	computed: {
		checkboxAppearanceRequiresHorizontal() {
			return (
				this.checkboxAppearance === 'bordered' ||
				this.checkboxAppearance === 'centered-card' ||
				this.checkboxAppearance === 'checkbox-card'
			)
		},

		inputRef() {
			return `label-${this.id.toString()}`
		},

		labelClasses() {
			return {
				'ca-checkbox__has-tooltip': this.showTooltip,
				'ca-checkbox__label': true,
				'whitespace-nowrap overflow-hidden text-ellipsis': this.truncateLabel,
			}
		},

		showTooltip() {
			return !!this.tooltip || !!this.truncatedLabelWidth
		},

		tooltipText() {
			if (!this.showTooltip) {
				return
			}

			return !!this.tooltip ? this.tooltip :
				!!this.label ? this.label : undefined
		},

		truncateLabel() {
			const truncatedDiv = document.getElementById(this.inputRef)
			const truncatedDivWidth = truncatedDiv?.clientWidth ?? 0

			return this.truncatedLabelWidth && this.truncatedLabelWidth >= truncatedDivWidth || false
		},

		truncateStyles() {
			return this.truncatedLabelWidth ? `width: ${this.truncatedLabelWidth}px` : ''
		},
	},

	mounted() {
		this.inputElement = this.$refs[this.inputRef] as HTMLInputElement

		if (this.inputElement) {
			this.inputElement.addEventListener('focus', this.handleFocus)
			this.inputElement.addEventListener('blur', this.handleBlur)
			this.inputElement.indeterminate = this.indeterminate
		}

		if (this.modelValue) {
			this.isFocused = true
		}
	},

	beforeUnmount() {
		if (this.inputElement) {
			this.inputElement.removeEventListener('focus', this.handleFocus)
			this.inputElement.removeEventListener('blur', this.handleBlur)
		}
	},

	watch: {
		indeterminate(newVal) {
			if (this.inputElement) {
				this.inputElement.indeterminate = newVal
			}
		},
	},

	methods: {
		handleBlur() {
			if (this.inputElement) {
				this.isFocused = false
				this.$emit('blur')
			}
		},

		handleFocus() {
			if (this.inputElement) {
				this.isFocused = true
				this.$emit('focus')
			}
		},

		handleKeydown(event: KeyboardEvent) {
			if (event.key === ' ' || event.key === 'Spacebar') {
				preventAndStop(event)
				this.$emit('update:modelValue', !this.modelValue)
			}

			if (event.key === 'Enter') {
				preventAndStop(event)
			}
		},
	},

	render() {
		const classes = {
			'ca-checkbox__option': true,
			'ca-checkbox__has-divider': !!this.divider,
			'ca-checkbox__use-borders': this.useBorders,
			'ca-checkbox__type--bordered': this.checkboxAppearance === 'bordered',
			'ca-checkbox__type--centered-card': this.checkboxAppearance === 'centered-card',
			'ca-checkbox__type--checkbox-card': this.checkboxAppearance === 'checkbox-card',
			'ca-checkbox__type--checkbox': this.checkboxAppearance === 'checkbox',
			'ca-checkbox__checked': !!this.modelValue,
			'ca-checkbox__disabled': this.disabled,
			'ca-checkbox__focused': this.isFocused,
		}

		const tagClasses = [
			'ca-checkbox__tags',
			{ 'sr-only': this.checkboxAppearanceRequiresHorizontal },
		]

		return (
			<div
				aria-describedby={this.description ? `${this.id}-description` : undefined}
				aria-label={this.label}
				class={classes}
			>
				<label class="ca-checkbox" for={this.id.toString()}>
					<div class="ca-checkbox__wrapper" data-test="ca-checkbox">
						<input
							ref={this.inputRef}
							aria-checked={this.indeterminate ? 'mixed' : this.modelValue}
							aria-describedby={this.description ? `${this.id}-description` : undefined}
							aria-labelledby={this.inputRef}
							checked={this.modelValue}
							class="ca-checkbox__input"
							data-test={this.dataTest}
							data-test-input="ca-checkbox-input"
							disabled={this.disabled}
							id={this.id.toString()}
							name={this.name}
							role="checkbox"
							tabindex={this.tabindex}
							type="checkbox"
							value={this.modelValue.toString()}
							onBlur={() => this.$emit('blur')}
							onFocus={() => this.$emit('focus')}
							onInput={() => this.$emit('update:modelValue', !this.modelValue)}
							onKeydown={(event: KeyboardEvent) => this.handleKeydown(event)}
						/>
					</div>

					<div class="w-full">
						<div
							class={{
								'ca-checkbox__label__content': true,
								'ca-checkbox__label__content--selected': !!this.modelValue,
							}}
							v-tooltip={this.tooltipText}
						>
							<div class="ca-checkbox__label__container">
								{this.$slots.label?.() ?
									this.$slots.label?.()

									:

									<span
										aria-describedby={this.description ? `${this.id}-description` : undefined}
										aria-label={`label-${this.id.toString()}`}
										class={this.labelClasses}
										id={`label-${this.id.toString()}`}
									>
										{this.avatar &&
											<CABadge
												appearance="avatar"
												data-test="ca-checkbox-badge-avatar"
												text={this.avatar}
											/>
										}

										<span class="ca-checkbox__content" style={this.truncateStyles}>
											{this.label}
										</span>
									</span>
								}

								{this.description &&
									<p
										class="ca-checkbox__description"
										data-test="ca-checkbox-description"
										id={`${this.id}-description`}
									>
										{this.description}
									</p>
								}
							</div>

							{this.tags &&
								<p
									class={tagClasses}
									data-test="ca-checkbox-tags"
									id={`${this.id}-tags`}
								>
									{this.tags.length && this.tagsLabel &&
										<span
											class="text-xs font-light text-gray-500 pr-2"
											data-test="ca-radio__tags-label"
										>
											{this.tagsLabel}
										</span>
									}

									{this.tags.map((tag, index) =>
										<CABadge
											key={index}
											badgeStyle="filled"
											text={tag}
											truncated={this.truncateBadges}
										/>
									)}
								</p>
							}
						</div>
					</div>

					{!this.checkboxAppearanceRequiresHorizontal && this.$slots.actions?.() &&
						this.$slots.actions?.()
					}
				</label>

				{this.divider && this.divider.show &&
					<CADivider
						mode={this.divider.mode}
						title={this.divider.title}
						type={this.checkboxAppearanceRequiresHorizontal ? 'vertical' : this.divider.type}
					/>
				}
			</div>
		)
	},
})

interface CAInputData {
	inputElement: HTMLInputElement | null
	isFocused: boolean
}
